import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import {
    User,
    Shield,
    Bell,
    Settings as SettingsIcon,
    Database,
    LogOut,
    ChevronRight,
    Eye,
    EyeOff,
    Save,
    Check,
    Smartphone,
    Mail,
    MessageSquare,
    Volume2,
    Sun,
    Moon,
    Globe,
    CreditCard,
    ArrowLeft,
    ShoppingBag,
    Wallet,
    MapPin,
    RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { authAPI, addressesAPI, subscriptionsAPI, ordersAPI } from '../lib/api';

const Settings = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('profile');
    const [submitting, setSubmitting] = useState(false);

    // Stats state
    const [stats, setStats] = useState({
        totalOrders: 0,
        walletBalance: 0,
        savedAddresses: 0,
        activeSubscriptions: 0
    });

    // Form states
    const [profileForm, setProfileForm] = useState({
        fullName: '',
        email: '',
        phone: ''
    });

    const [securityForm, setSecurityForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const [notificationForm, setNotificationForm] = useState({
        orderStatusUpdates: true,
        deliveryAlerts: true,
        subscriptionReminders: true,
        promotionalOffers: true,
        email: true,
        sms: true,
        push: true,
        sound: true
    });

    const [preferenceForm, setPreferenceForm] = useState({
        theme: 'light',
        language: 'English',
        defaultPaymentMethod: 'wallet',
        autoApplyOffers: true
    });

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        fetchUserData();
    }, [navigate]);

    const fetchUserData = async () => {
        try {
            const response = await authAPI.getMe();
            const userData = response.data;
            setUser(userData);

            // Initialize forms
            setProfileForm({
                fullName: userData.fullName || '',
                email: userData.email || '',
                phone: userData.phone || ''
            });

            if (userData.notificationPreferences) {
                setNotificationForm({ ...userData.notificationPreferences });
            }

            if (userData.appPreferences) {
                setPreferenceForm({ ...userData.appPreferences });
            }

            // Fetch stats - different for delivery role
            if (userData.role === 'delivery') {
                const ordersRes = await ordersAPI.getMyDeliveries();
                setStats({
                    totalOrders: ordersRes.data.length,
                    walletBalance: userData.wallet?.balance || 0,
                    savedAddresses: 0,
                    activeSubscriptions: 0
                });
            } else {
                const [ordersRes, addressesRes, subsRes] = await Promise.all([
                    ordersAPI.getAll(),
                    addressesAPI.getAll(),
                    subscriptionsAPI.getAll()
                ]);

                setStats({
                    totalOrders: ordersRes.data.length,
                    walletBalance: userData.wallet?.balance || 0,
                    savedAddresses: addressesRes.data.length,
                    activeSubscriptions: subsRes.data.filter((s: any) => s.status === 'active').length
                });
            }

        } catch (error) {
            console.error('Error fetching user data:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleProfileUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const response = await authAPI.updateProfile({
                fullName: profileForm.fullName,
                email: profileForm.email,
                phone: profileForm.phone
            });
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            toast.success('Profile updated successfully');
        } catch (error) {
            toast.error('Failed to update profile');
        } finally {
            setSubmitting(false);
        }
    };

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        if (securityForm.newPassword !== securityForm.confirmNewPassword) {
            toast.error('New passwords do not match');
            return;
        }
        setSubmitting(true);
        try {
            await authAPI.resetPassword(
                user.email,
                securityForm.newPassword,
                securityForm.confirmNewPassword,
                securityForm.currentPassword
            );
            toast.success('Password changed successfully');
            setSecurityForm({
                currentPassword: '',
                newPassword: '',
                confirmNewPassword: ''
            });
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to change password');
        } finally {
            setSubmitting(false);
        }
    };

    const handleNotificationUpdate = async () => {
        setSubmitting(true);
        try {
            const response = await authAPI.updateProfile({
                notificationPreferences: notificationForm
            } as any);
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            toast.success('Notification preferences saved');
        } catch (error) {
            toast.error('Failed to save preferences');
        } finally {
            setSubmitting(false);
        }
    };

    const handlePreferenceUpdate = async () => {
        setSubmitting(true);
        try {
            const response = await authAPI.updateProfile({
                appPreferences: preferenceForm
            } as any);
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            toast.success('App preferences saved');
        } catch (error) {
            toast.error('Failed to save preferences');
        } finally {
            setSubmitting(false);
        }
    };

    const handleLogout = async () => {
        try {
            await authAPI.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        toast.success('Logged out successfully');
        navigate('/login');
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10b981]"></div>
                </div>
            </Layout>
        );
    }

    const tabs = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'preferences', label: 'Preferences', icon: SettingsIcon },
        { id: 'data', label: 'My Data', icon: Database },
    ];

    return (
        <Layout>
            <div className="max-w-6xl mx-auto">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-500 hover:text-gray-800 mb-4 transition-colors"
                >
                    <ArrowLeft size={18} />
                    <span>Back</span>
                </button>

                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-800">Settings</h1>
                    <p className="text-gray-500">Manage your account and preferences</p>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar */}
                    <aside className="w-full lg:w-64 shrink-0">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="p-6 bg-[#10b981] flex flex-col items-center">
                                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center text-white mb-3">
                                    <User size={40} />
                                </div>
                                <h3 className="text-white font-bold text-lg truncate w-full text-center">
                                    {user?.fullName || 'User'}
                                </h3>
                            </div>

                            <nav className="p-2">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${activeTab === tab.id
                                            ? 'bg-[#10b981]/10 text-[#10b981]'
                                            : 'text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <tab.icon size={20} />
                                            <span className="font-medium">{tab.label}</span>
                                        </div>
                                        <ChevronRight size={16} className={activeTab === tab.id ? 'opacity-100' : 'opacity-0'} />
                                    </button>
                                ))}

                                <div className="my-2 border-t border-gray-100"></div>

                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                >
                                    <LogOut size={20} />
                                    <span className="font-medium">Logout</span>
                                </button>
                            </nav>
                        </div>
                    </aside>

                    {/* Content Area */}
                    <main className="flex-1">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 lg:p-8 min-h-[500px]">

                            {/* Profile Tab */}
                            {activeTab === 'profile' && (
                                <div className="animate-fade-in">
                                    <h2 className="text-xl font-bold text-gray-800 mb-6">Profile Information</h2>
                                    <form onSubmit={handleProfileUpdate} className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                            <input
                                                type="text"
                                                value={profileForm.fullName}
                                                onChange={(e) => setProfileForm({ ...profileForm, fullName: e.target.value })}
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                            <input
                                                type="email"
                                                disabled
                                                value={profileForm.email}
                                                className="w-full px-4 py-2.5 border border-gray-100 bg-gray-50 text-gray-500 rounded-xl cursor-not-allowed"
                                            />
                                            <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                            <input
                                                type="tel"
                                                value={profileForm.phone}
                                                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                                                placeholder="Enter phone number"
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] outline-none"
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className="bg-[#10b981] text-white px-6 py-2.5 rounded-xl font-bold hover:bg-[#059669] transition-all flex items-center gap-2"
                                        >
                                            <Save size={18} />
                                            {submitting ? 'Saving...' : 'Save Changes'}
                                        </button>
                                    </form>

                                    {user?.role !== 'delivery' && (
                                        <div className="mt-12">
                                            <h3 className="text-lg font-bold text-gray-800 mb-6">Account Overview</h3>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div className="bg-gray-50 p-4 rounded-2xl flex flex-col items-center text-center">
                                                    <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mb-2">
                                                        <ShoppingBag size={20} />
                                                    </div>
                                                    <span className="text-2xl font-bold text-gray-800">{stats.totalOrders}</span>
                                                    <span className="text-xs text-gray-500">Total Orders</span>
                                                </div>
                                                <div className="bg-gray-50 p-4 rounded-2xl flex flex-col items-center text-center">
                                                    <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center mb-2">
                                                        <Wallet size={20} />
                                                    </div>
                                                    <span className="text-2xl font-bold text-gray-800">₹{stats.walletBalance}</span>
                                                    <span className="text-xs text-gray-500">Wallet Balance</span>
                                                </div>
                                                <div className="bg-gray-50 p-4 rounded-2xl flex flex-col items-center text-center">
                                                    <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center mb-2">
                                                        <MapPin size={20} />
                                                    </div>
                                                    <span className="text-2xl font-bold text-gray-800">{stats.savedAddresses}</span>
                                                    <span className="text-xs text-gray-500">Saved Addresses</span>
                                                </div>
                                                <div className="bg-gray-50 p-4 rounded-2xl flex flex-col items-center text-center">
                                                    <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center mb-2">
                                                        <RefreshCw size={20} />
                                                    </div>
                                                    <span className="text-2xl font-bold text-gray-800">{stats.activeSubscriptions}</span>
                                                    <span className="text-xs text-gray-500">Active Subscriptions</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Security Tab */}
                            {activeTab === 'security' && (
                                <div className="animate-fade-in">
                                    <h2 className="text-xl font-bold text-gray-800 mb-6">Change Password</h2>
                                    <form onSubmit={handlePasswordChange} className="space-y-6 max-w-md">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                                            <div className="relative">
                                                <input
                                                    type={showPasswords.current ? 'text' : 'password'}
                                                    value={securityForm.currentPassword}
                                                    onChange={(e) => setSecurityForm({ ...securityForm, currentPassword: e.target.value })}
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] outline-none pr-10"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                >
                                                    {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                                            <div className="relative">
                                                <input
                                                    type={showPasswords.new ? 'text' : 'password'}
                                                    value={securityForm.newPassword}
                                                    onChange={(e) => setSecurityForm({ ...securityForm, newPassword: e.target.value })}
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] outline-none pr-10"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                >
                                                    {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                                            <div className="relative">
                                                <input
                                                    type={showPasswords.confirm ? 'text' : 'password'}
                                                    value={securityForm.confirmNewPassword}
                                                    onChange={(e) => setSecurityForm({ ...securityForm, confirmNewPassword: e.target.value })}
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] outline-none pr-10"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                >
                                                    {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className="bg-[#10b981] text-white px-6 py-2.5 rounded-xl font-bold hover:bg-[#059669] transition-all flex items-center gap-2"
                                        >
                                            <Shield size={18} />
                                            {submitting ? 'Changing...' : 'Change Password'}
                                        </button>
                                    </form>

                                    <div className="mt-12 p-6 bg-gray-50 rounded-2xl">
                                        <h3 className="text-lg font-bold text-gray-800 mb-6">Security Information</h3>
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <Smartphone size={20} className="text-gray-400" />
                                                    <span className="text-gray-700">Two-Factor Authentication</span>
                                                </div>
                                                <span className="text-orange-500 text-sm font-medium">Not enabled</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <Shield size={20} className="text-gray-400" />
                                                    <span className="text-gray-700">Account Status</span>
                                                </div>
                                                <span className="text-[#10b981] text-sm font-medium flex items-center gap-1">
                                                    <Check size={14} /> Secure
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <RefreshCw size={20} className="text-gray-400" />
                                                    <span className="text-gray-700">Last Login</span>
                                                </div>
                                                <span className="text-gray-500 text-sm">Just now</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Notifications Tab */}
                            {activeTab === 'notifications' && (
                                <div className="animate-fade-in">
                                    <h2 className="text-xl font-bold text-gray-800 mb-6">Notification Preferences</h2>

                                    <div className="space-y-8">
                                        <section>
                                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Order & Delivery</h3>
                                            <div className="space-y-4">
                                                {[
                                                    { id: 'orderStatusUpdates', label: 'Order status updates', desc: 'Get notified when your order status changes' },
                                                    { id: 'deliveryAlerts', label: 'Delivery alerts', desc: 'Real-time updates when delivery is nearby' },
                                                    { id: 'subscriptionReminders', label: 'Subscription reminders', desc: 'Reminders before subscription deliveries' }
                                                ].map((item) => (
                                                    <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                                                        <div>
                                                            <p className="font-bold text-gray-800">{item.label}</p>
                                                            <p className="text-xs text-gray-500">{item.desc}</p>
                                                        </div>
                                                        <input
                                                            type="checkbox"
                                                            checked={(notificationForm as any)[item.id]}
                                                            onChange={(e) => setNotificationForm({ ...notificationForm, [item.id]: e.target.checked })}
                                                            className="w-5 h-5 text-[#10b981] rounded focus:ring-[#10b981]"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </section>

                                        <section>
                                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Marketing</h3>
                                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                                                <div>
                                                    <p className="font-bold text-gray-800">Promotional offers</p>
                                                    <p className="text-xs text-gray-500">Deals, discounts, and special offers</p>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={notificationForm.promotionalOffers}
                                                    onChange={(e) => setNotificationForm({ ...notificationForm, promotionalOffers: e.target.checked })}
                                                    className="w-5 h-5 text-[#10b981] rounded focus:ring-[#10b981]"
                                                />
                                            </div>
                                        </section>

                                        <section>
                                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Channels</h3>
                                            <div className="space-y-3">
                                                {[
                                                    { id: 'email', label: 'Email', icon: Mail },
                                                    { id: 'sms', label: 'SMS', icon: MessageSquare },
                                                    { id: 'push', label: 'Push Notifications', icon: Bell },
                                                    { id: 'sound', label: 'Sound', icon: Volume2 }
                                                ].map((item) => (
                                                    <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                                                        <div className="flex items-center gap-3">
                                                            <item.icon size={20} className="text-gray-400" />
                                                            <span className="font-bold text-gray-800">{item.label}</span>
                                                        </div>
                                                        <input
                                                            type="checkbox"
                                                            checked={(notificationForm as any)[item.id]}
                                                            onChange={(e) => setNotificationForm({ ...notificationForm, [item.id]: e.target.checked })}
                                                            className="w-5 h-5 text-[#10b981] rounded focus:ring-[#10b981]"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </section>

                                        <button
                                            onClick={handleNotificationUpdate}
                                            disabled={submitting}
                                            className="bg-[#10b981] text-white px-6 py-2.5 rounded-xl font-bold hover:bg-[#059669] transition-all flex items-center gap-2"
                                        >
                                            <Save size={18} />
                                            {submitting ? 'Saving...' : 'Save Preferences'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Preferences Tab */}
                            {activeTab === 'preferences' && (
                                <div className="animate-fade-in">
                                    <h2 className="text-xl font-bold text-gray-800 mb-6">App Preferences</h2>

                                    <div className="space-y-8">
                                        <section>
                                            <h3 className="text-sm font-bold text-gray-700 mb-3">Theme</h3>
                                            <div className="flex gap-4">
                                                <button
                                                    onClick={() => setPreferenceForm({ ...preferenceForm, theme: 'light' })}
                                                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-all ${preferenceForm.theme === 'light'
                                                        ? 'border-[#10b981] bg-[#10b981]/5 text-[#10b981]'
                                                        : 'border-gray-100 bg-gray-50 text-gray-500'
                                                        }`}
                                                >
                                                    <Sun size={20} />
                                                    <span className="font-bold">Light</span>
                                                </button>
                                                <button
                                                    onClick={() => setPreferenceForm({ ...preferenceForm, theme: 'dark' })}
                                                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-all ${preferenceForm.theme === 'dark'
                                                        ? 'border-[#10b981] bg-[#10b981]/5 text-[#10b981]'
                                                        : 'border-gray-100 bg-gray-50 text-gray-500'
                                                        }`}
                                                >
                                                    <Moon size={20} />
                                                    <span className="font-bold">Dark</span>
                                                </button>
                                            </div>
                                        </section>

                                        <section>
                                            <h3 className="text-sm font-bold text-gray-700 mb-3">Language</h3>
                                            <div className="relative">
                                                <Globe size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <select
                                                    value={preferenceForm.language}
                                                    onChange={(e) => setPreferenceForm({ ...preferenceForm, language: e.target.value })}
                                                    className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] outline-none appearance-none bg-white"
                                                >
                                                    <option>English</option>
                                                    <option>Hindi</option>
                                                    <option>Spanish</option>
                                                    <option>French</option>
                                                </select>
                                                <ChevronRight size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 rotate-90" />
                                            </div>
                                        </section>

                                        <section>
                                            <h3 className="text-sm font-bold text-gray-700 mb-3">Default Payment Method</h3>
                                            <div className="relative">
                                                <CreditCard size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <select
                                                    value={preferenceForm.defaultPaymentMethod}
                                                    onChange={(e) => setPreferenceForm({ ...preferenceForm, defaultPaymentMethod: e.target.value })}
                                                    className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] outline-none appearance-none bg-white"
                                                >
                                                    <option value="wallet">Wallet</option>
                                                    <option value="card">Credit/Debit Card</option>
                                                    <option value="upi">UPI</option>
                                                    <option value="cod">Cash on Delivery</option>
                                                </select>
                                                <ChevronRight size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 rotate-90" />
                                            </div>
                                        </section>

                                        <section className="p-4 bg-gray-50 rounded-2xl flex items-center justify-between">
                                            <div>
                                                <p className="font-bold text-gray-800">Auto-apply offers</p>
                                                <p className="text-xs text-gray-500">Automatically apply best available offers at checkout</p>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={preferenceForm.autoApplyOffers}
                                                onChange={(e) => setPreferenceForm({ ...preferenceForm, autoApplyOffers: e.target.checked })}
                                                className="w-5 h-5 text-[#10b981] rounded focus:ring-[#10b981]"
                                            />
                                        </section>

                                        <button
                                            onClick={handlePreferenceUpdate}
                                            disabled={submitting}
                                            className="bg-[#10b981] text-white px-6 py-2.5 rounded-xl font-bold hover:bg-[#059669] transition-all flex items-center gap-2"
                                        >
                                            <Save size={18} />
                                            {submitting ? 'Saving...' : 'Save Preferences'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Data Tab */}
                            {activeTab === 'data' && (
                                <div className="animate-fade-in">
                                    <h2 className="text-xl font-bold text-gray-800 mb-6">My Data</h2>
                                    <p className="text-gray-500 mb-8">Manage your personal data and account privacy.</p>

                                    <div className="space-y-4">
                                        <button className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-all">
                                            <div className="text-left">
                                                <p className="font-bold text-gray-800">Download My Data</p>
                                                <p className="text-xs text-gray-500">Get a copy of your account information</p>
                                            </div>
                                            <ChevronRight size={20} className="text-gray-400" />
                                        </button>
                                        <button className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-all">
                                            <div className="text-left">
                                                <p className="font-bold text-gray-800">Privacy Policy</p>
                                                <p className="text-xs text-gray-500">Read our terms and privacy policy</p>
                                            </div>
                                            <ChevronRight size={20} className="text-gray-400" />
                                        </button>
                                        <button className="w-full flex items-center justify-between p-4 bg-red-50 rounded-2xl hover:bg-red-100 transition-all">
                                            <div className="text-left">
                                                <p className="font-bold text-red-600">Delete Account</p>
                                                <p className="text-xs text-red-400">Permanently remove your account and data</p>
                                            </div>
                                            <ChevronRight size={20} className="text-red-400" />
                                        </button>
                                    </div>
                                </div>
                            )}

                        </div>
                    </main>
                </div>
            </div>
        </Layout>
    );
};

export default Settings;